<template>
  <div
    :class="[
      bemClass(),
      bemClass('', currentState),
      {
        [bemClass('', 'custom-player')]: isCustomPlayer,
        [bemClass('', 'hovered')]: isHovered,
      }
    ]"
    @mouseout="handleHover(false)"
    @mouseover="handleHover(true)"
    @mousemove="handleHover(true)"
  >
    <div ref="youtubeWrapper" :class="bemClass('wrapper')">
      <div ref="youtubeRef" :class="bemClass('video')"></div>
      <div v-if="isCustomPlayer" :class="bemClass('custom')">
        <div :class="bemClass('custom-background')">
          <slot name="background"></slot>
        </div>
        <sel-button :class="bemClass('custom-play-pause')" @click="handlePlayPause">
          <slot v-if="currentState === 'playing'" name="pause">pause</slot>
          <slot v-else name="play">play</slot>
        </sel-button>
        <sel-button :class="bemClass('custom-mute')" @click="handleMute">
          <slot v-if="isMuted" name="mute">Unmute</slot>
          <slot v-else name="unmute">Mute</slot>
        </sel-button>
        <div :class="bemClass('custom-progress')">
          <div :style="{ transform: `scaleX(${playingProgress})` }" :class="bemClass('custom-progress-bar')"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import { onMounted, ref, toRefs, watch } from 'vue'
import YouTubePlayer from 'youtube-player'
import { getIdFromUrl } from '../../util/youtube'
import useBem from '../../compositions/bem'

const UNSTARTED = -1
const ENDED = 0
const PLAYING = 1
const PAUSED = 2
const BUFFERING = 3
const CUED = 5

export default {
  props: {
    isCustomPlayer: Boolean,
    customHoverTimeout: {
      type: Number,
      default: 3000
    },
    disableLazyLoad: Boolean,
    url: {
      type: String,
      required: true
    },
    playerVars: {
      type: Object,
      default: () => ({})
    },
    modifier: {
      type: String,
      default: ''
    }
  },
  setup (props, { emit }) {
    const { modifier } = toRefs(props)
    const events = {
      [UNSTARTED]: 'unstarted',
      [PLAYING]: 'playing',
      [PAUSED]: 'paused',
      [ENDED]: 'ended',
      [BUFFERING]: 'buffering',
      [CUED]: 'cued'
    }
    const playingTimer = ref(null)
    const hoverTimer = ref(null)
    const currentState = ref('')
    const isLoaded = ref(false)
    const isHovered = ref(false)
    const player = ref({})
    const youtubeWrapper = ref(null)
    const youtubeRef = ref(null)
    const videoId = getIdFromUrl(props.url)
    const isMuted = ref(false)
    const playingProgress = ref(0)
    const loadVideo = () => {
      const playerVars = Object.assign({}, props.playerVars, { controls: props.isCustomPlayer ? 0 : 1 })
      if (!isLoaded.value) {
        player.value = YouTubePlayer(youtubeRef.value, {
          videoId,
          playerVars
        })
        isLoaded.value = true

        player.value.on('ready', playerReady)
        player.value.on('stateChange', playerStateChange)
        player.value.on('error', playerError)
        isMuted.value = player.value.isMuted()
      }
    }
    const playerReady = (e) => {
      emit('ready', e.target)
      currentState.value = 'ready'
      isMuted.value = player.value.isMuted()
    }
    const playerStateChange = (e) => {
      if (e.data !== null && e.data !== UNSTARTED) {
        emit(events[e.data], e.target)
        currentState.value = events[e.data]
        if (e.data === PLAYING) {
          handleProgress()
        }
      }
    }
    const playerError = (e) => {
      emit('error', e.target)
      currentState.value = 'error'
    }
    const handlePlayPause = () => {
      if (isLoaded.value && player.value) {
        if (currentState.value === events[PLAYING]) {
          player.value.pauseVideo()
        } else {
          player.value.playVideo()
        }
      }
    }
    const handleMute = () => {
      if (player.value) {
        isMuted.value = !isMuted.value
        if (isMuted.value) {
          player.value.mute()
        } else {
          player.value.unMute()
        }
      }
    }
    const handleProgress = async () => {
      if (player.value) {
        const duration = await player.value.getDuration()
        const currentTime = await player.value.getCurrentTime()
        playingProgress.value = currentTime / duration
      }
    }
    const handleHover = value => {
      isHovered.value = value
      clearTimeout(hoverTimer.value)
      if (value) {
        hoverTimer.value = setTimeout(() => {
          handleHover(false)
        }, props.customHoverTimeout)
      }
    }

    watch(currentState, newValue => {
      if (newValue === events[PLAYING]) {
        playingTimer.value = setInterval(handleProgress, 1000)
      } else if (newValue === events[ENDED]) {
        clearInterval(playingTimer.value)
        playingProgress.value = 1
      } else {
        clearInterval(playingTimer.value)
      }
    })

    onMounted(() => {
      if (props.disableLazyLoad) {
        loadVideo()
      } else {
        const intersectionObserverCallback = (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              loadVideo()
            }
          })
        }
        const intersectionObserver = new IntersectionObserver(intersectionObserverCallback)
        intersectionObserver.observe(youtubeWrapper.value)
      }
    })

    const { bemClass } = useBem('sel-youtube-video', modifier)

    return {
      bemClass,
      youtubeRef,
      youtubeWrapper,
      player,
      currentState,
      handlePlayPause,
      isMuted,
      handleMute,
      playingProgress,
      handleHover,
      isHovered
    }
  }
}
</script>

<style>
.sel-youtube-video {
  position: relative;
}

.sel-youtube-video__wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.sel-youtube-video__video {
  width: 100%;
  height: 100%;
  opacity: 1;
  transition: opacity 300ms ease-in;

  .sel-youtube-video--custom-player.sel-youtube-video--ready & {
    opacity: 0;
  }
}

.sel-youtube-video__custom {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 1;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1;
  transition: opacity 300ms ease-in;

  .sel-youtube-video--playing:not(.sel-youtube-video--hovered) & {
    opacity: 0;
  }
}

.sel-youtube-video__custom-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;

  .sel-youtube-video--playing &,
  .sel-youtube-video--paused &,
  .sel-youtube-video--buffering & {
    display: none;
  }
}

.sel-youtube-video__custom-progress {
  position: relative;
  width: 100%;
}

.sel-youtube-video__custom-progress-bar {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transform-origin: left;
  transition: transform 100ms ease-in-out;
}
</style>
